import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';

const button = 'border border-zinc-300 px-3 py-2 text-sm disabled:opacity-50 hover:bg-zinc-50';
const input = 'w-full border border-zinc-300 bg-white p-2 text-sm';
const time = value => value ? new Date(value).toLocaleString() : 'Not checked';
const amount = (value, unit) => unit === 'credits' ? `${Number(value).toLocaleString()} credits`
  : new Intl.NumberFormat('en-IN', { style: 'currency', currency: unit, maximumFractionDigits: 4 }).format(Number(value));
const healthLabels = {
  not_configured: 'API key not configured', no_recent_calls: 'No calls recorded in 30 days',
  last_call_succeeded: 'Last recorded call succeeded', last_call_failed: 'Last recorded call failed',
};

function ProviderCard({ item, onSaved }) {
  const [threshold, setThreshold] = useState(item.settings.threshold);
  const [currency, setCurrency] = useState(item.settings.currency);
  const [balance, setBalance] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [failed, setFailed] = useState(false);
  const danger = item.low_balance || item.account_available === false || item.health === 'last_call_failed' || !item.configured;
  async function save(event) {
    event.preventDefault();
    setSaving(true); setMessage(''); setFailed(false);
    try {
      const { data } = await api.put(`/admin/providers/${item.provider}/settings`, {
        threshold, currency, balance: balance === '' ? null : balance,
      });
      onSaved(data); setBalance(''); setMessage('Settings saved.');
    } catch (error) {
      setFailed(true); setMessage(typeof error.response?.data?.detail === 'string'
        ? error.response.data.detail : 'Could not save monitoring settings.');
    } finally { setSaving(false); }
  }
  return <article className="border border-zinc-200 p-5 space-y-3" aria-label={`${item.name} monitoring`}>
    <div className="flex items-start justify-between gap-2">
      <h3 className="text-lg font-semibold">{item.name}</h3>
      <span className={`text-xs px-2 py-1 ${danger ? 'bg-red-50 text-red-700' : 'bg-zinc-100 text-zinc-700'}`}>
        {healthLabels[item.health]}
      </span>
    </div>
    {item.account_available === false && <p className="text-red-700 text-sm">Provider reports this account is unavailable for API calls.</p>}
    {item.low_balance && <p className="text-red-700 font-medium text-sm">Low balance or allowance — review top-up.</p>}
    <p className="text-xs uppercase tracking-wide text-zinc-500">{item.balance_mode === 'live' ? 'Provider API balance / allowance' : 'Manual balance snapshot'}</p>
    {item.balances.length ? <ul className="space-y-1">
      {item.balances.map((entry, index) => <li key={`${entry.currency}-${index}`}>
        <strong>{amount(entry.remaining, entry.currency)}</strong>
        <span className="text-sm text-zinc-600"> · {entry.label}</span>
        {entry.limit != null && <div className="text-xs text-zinc-500">Used {entry.used} of {entry.limit} credits</div>}
      </li>)}
    </ul> : <p className="text-sm">Balance unknown</p>}
    <p className="text-xs text-zinc-500">Balance recorded: {time(item.checked_at)}{item.stale ? ' · Stale / not checked' : ''}</p>
    {item.balances.length > 0 && !item.balances.some(entry => entry.currency === item.settings.currency) && <p className="text-sm text-amber-800">No balance returned in {item.settings.currency}. Select a returned currency below to enable a matching threshold alert.</p>}
    {item.refresh_error && <p className="text-sm text-amber-800">Balance refresh failed: {item.refresh_error.replaceAll('_', ' ')}. Any balance above is the last saved value.</p>}
    {item.balance_mode === 'manual' && <p className="text-xs text-zinc-600">No live balance integration. Confirm the balance in the billing console and record it below. This snapshot does not decrease automatically.</p>}
    {item.provider === 'tavily' && <p className="text-xs text-zinc-600">Key, plan and pay-as-you-go limits are separate; they are not added together. Limits are not a prepaid cash balance. Alerts cover key and plan allowances. Missing or unlimited allowances are not shown as zero.</p>}
    <div className="border-t border-zinc-100 pt-3 text-sm space-y-1">
      <p>Estimated recorded spend, 30 days: <strong>{amount(item.known_cost_usd_30d, 'USD')}</strong>{item.unknown_cost_calls_30d > 0 ? ' + unknown costs' : ''}</p>
      <p>{item.calls_30d} calls · {item.failures_30d} failures · {item.unknown_cost_calls_30d} calls with unknown cost</p>
      <p className="text-xs text-zinc-500">Last call: {time(item.last_call_at)}{item.last_error ? ` · ${item.last_error.replaceAll('_', ' ')}` : ''}</p>
      {item.last_success_at && <p className="text-xs text-zinc-500">Last successful call: {time(item.last_success_at)}</p>}
    </div>
    <form onSubmit={save} className="border-t border-zinc-100 pt-3 space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <label className="text-xs">Alert at or below
          <input className={input} aria-label={`${item.name} low balance threshold`} type="number" min="0" max="1000000000000" step="any" required value={threshold} onChange={e => setThreshold(e.target.value)} />
        </label>
        <label className="text-xs">Unit
          <select className={input} aria-label={`${item.name} balance unit`} value={currency} onChange={e => setCurrency(e.target.value)}>
            {item.provider === 'tavily' ? <option value="credits">Credits</option> : <option value="USD">USD</option>}
            {item.provider === 'deepseek' && <option value="CNY">CNY</option>}
          </select>
        </label>
      </div>
      {item.balance_mode === 'manual' && <label className="block text-xs">New balance confirmed in console (optional, USD)
        <input className={input} aria-label={`${item.name} confirmed balance`} type="number" min="0" max="1000000000000" step="any" value={balance} onChange={e => setBalance(e.target.value)} placeholder="Leave blank to keep the previous snapshot" />
      </label>}
      <div className="flex items-center justify-between gap-2">
        <button className={button} disabled={saving}>{saving ? 'Saving…' : 'Save settings'}</button>
        <a className="text-sm text-[#002FA7] underline" href={item.billing_url} target="_blank" rel="noopener noreferrer">Billing console ↗</a>
      </div>
      {message && <p role="status" className={`text-sm ${failed ? 'text-red-700' : 'text-green-700'}`}>{message}</p>}
    </form>
  </article>;
}

export default function ProviderHealthPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const load = useCallback(async (refresh = false) => {
    setBusy(true); setError('');
    try {
      const response = refresh ? await api.post('/admin/providers/refresh') : await api.get('/admin/providers');
      setData(response.data);
    } catch { setError('Provider monitoring could not be loaded. Please retry.'); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => {
    load(true);
    const timer = setInterval(() => { if (!document.hidden) load(true); }, 300000);
    return () => clearInterval(timer);
  }, [load]);
  const alerts = data?.providers.filter(p => p.low_balance || p.account_available === false || p.health === 'last_call_failed' || !p.configured || p.refresh_error || p.stale) || [];
  return <section className="mt-10 border-t border-zinc-200 pt-6" aria-label="Provider balances and health">
    <div className="flex items-center justify-between gap-4 mb-3">
      <h2 className="text-xl font-semibold">Provider balances and health</h2>
      <button className={button} disabled={busy} onClick={() => load(true)}>{busy ? 'Refreshing…' : 'Refresh balances & health'}</button>
    </div>
    <p className="text-sm text-zinc-600 mb-4">Balances are shared across models in each provider account. Live checks refresh at most every five minutes while this page is open. Health reflects recorded report calls, not a live inference test. No paid test generations are made.</p>
    <p className="text-xs text-zinc-500 mb-4">Spend covers this environment’s recorded report usage only, including failed attempts when costs are known. Other environments and untracked calls are excluded. A successful previous call does not guarantee current availability.</p>
    {alerts.length > 0 && <p role="status" className="border border-amber-300 bg-amber-50 p-3 mb-4 text-sm text-amber-900">Attention needed: {alerts.map(p => p.name).join(', ')}. Review balances and the latest failures below.</p>}
    {error && <p role="alert" className="text-red-700 mb-4">{error}</p>}
    {data && <><p className="text-xs text-zinc-500 mb-3">Dashboard updated: {time(data.checked_at)}</p><div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">{data.providers.map(item => <ProviderCard key={item.provider} item={item} onSaved={setData} />)}</div></>}
  </section>;
}
